import React, { useMemo, useCallback, useState, useEffect } from 'react';
import {
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  StepContent,
  Grid,
  CheckIcon,
  Collapse,
  useMediaQuery,
} from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import useSideBarContent from '@modules/ui/shared/hooks/useSideBarContent';
import useCourseProgressBarStyles from '@modules/learning/components/CourseProgressBar.styles';
import TreeNode from '@modules/ui/shared/interfaces/TreeNode';
import { rightNavID } from '@modules/layout/layout/constants/layoutConstants';
import learningClient from '@modules/clients/learningClient';
import { MilestoneNode, Status } from '@modules/learning/types/learning';
import {
  getLessonDuration,
  processMilestones,
  findSelectedMilestoneIndex,
  getMilestoneProgressInfo,
  getCourseLinkForLocale,
} from '@modules/learning/utils/courseUtils';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import useCurrentLocale from '@modules/localization/hooks/useCurrentLocale';
import { getLocalLearningProgress } from '@modules/learning/utils/localStorageSync';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import DocsCoursesEvent from '@modules/eventStream/enum/DocsCoursesEvent';
import { coursesRootPath } from '../constants/coursesNavConstants';
import CourseTitleWithMenu from './CourseTitleWithMenu';

export default function CourseProgressBar() {
  const { classes } = useCourseProgressBarStyles();
  const { translate } = useTranslation();
  const { treeListData, selected } = useSideBarContent();
  const { targetLocale } = useCurrentLocale();
  const [previousMilestoneIndex, setPreviousMilestoneIndex] = useState(-1);
  const [lessonStatuses, setLessonStatuses] = useState<Record<string, Status>>({});
  const [manuallyExpanded, setManuallyExpanded] = useState<Set<string>>(new Set());
  const [showResetAlert, setShowResetAlert] = useState(false);

  const showMobileMenu = useMediaQuery((theme) => theme.breakpoints.down('Large'));

  const loadProgress = useCallback(async () => {
    try {
      const localProgress = getLocalLearningProgress();
      setLessonStatuses(localProgress);

      const serverProgress = await learningClient.getLessonProgress();
      setLessonStatuses(serverProgress);
    } catch (err) {
      console.error('Failed to load lesson progress:', err);
      const fallbackProgress = getLocalLearningProgress();
      setLessonStatuses(fallbackProgress);
    }
  }, []);

  useEffect(() => {
    const handleProgressUpdate = () => {
      loadProgress();
    };

    window.addEventListener('learningProgressUpdate', handleProgressUpdate);

    return () => {
      window.removeEventListener('learningProgressUpdate', handleProgressUpdate);
    };
  }, [loadProgress]);

  const { milestones } = useMemo(() => {
    const processedMilestones = processMilestones(treeListData);

    return {
      milestones: processedMilestones,
    };
  }, [treeListData]);

  const selectedMilestoneIndex = useMemo(
    () => findSelectedMilestoneIndex(milestones, selected),
    [milestones, selected],
  );

  const selectedMilestoneId = useMemo(
    () => milestones[selectedMilestoneIndex]?.id || null,
    [selectedMilestoneIndex, milestones],
  );

  useEffect(() => {
    setManuallyExpanded(new Set());
  }, [selectedMilestoneId]);

  const { currentMilestoneIndex, milestoneCompletions, milestoneActivity } = useMemo(() => {
    const progressInfo = getMilestoneProgressInfo(milestones, lessonStatuses);
    return {
      currentMilestoneIndex: progressInfo.currentMilestone,
      milestoneCompletions: milestones.map((_, index) => progressInfo.isMilestoneCompleted(index)),
      milestoneActivity: milestones.map((_, index) => progressInfo.isMilestoneActive(index)),
    };
  }, [milestones, lessonStatuses]);

  const canExpand = useMemo(
    () => milestones.map((_, index) => milestoneActivity[index] || milestoneCompletions[index]),
    [milestoneActivity, milestoneCompletions, milestones],
  );

  const lessonsWithDuration = useMemo(() => {
    const durationMap = new Map();
    milestones.forEach((milestone) => {
      milestone.children?.forEach((lesson) => {
        if (lesson.path) {
          const duration = getLessonDuration(lesson.path);
          if (duration) {
            durationMap.set(lesson.id, duration);
          }
        }
      });
    });
    return durationMap;
  }, [milestones]);

  useEffect(() => {
    if (selectedMilestoneIndex !== previousMilestoneIndex) {
      setPreviousMilestoneIndex(selectedMilestoneIndex);
      loadProgress();
    }
  }, [selectedMilestoneIndex, previousMilestoneIndex, loadProgress]);

  const handleMilestoneClick = useCallback(
    (milestone: MilestoneNode, index: number) => {
      unifiedLoggerClient.logClickEvent({
        eventName: DocsCoursesEvent.clickCourseProgressBar,
        parameters: {
          locale: targetLocale ? String(targetLocale) : '',
          milestoneId: milestone.id,
        },
      });

      if (!canExpand[index]) return;

      setManuallyExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(milestone.id)) {
          next.delete(milestone.id);
        } else {
          next.add(milestone.id);
        }
        return next;
      });
    },
    [targetLocale, canExpand],
  );

  const handleLessonClick = useCallback(
    (lesson: TreeNode, milestone: MilestoneNode) => {
      unifiedLoggerClient.logClickEvent({
        eventName: DocsCoursesEvent.clickCourseProgressBar,
        parameters: {
          locale: targetLocale ? String(targetLocale) : '',
          lessonId: lesson.id,
          lessonTitle: lesson.value || '',
          milestoneId: milestone.id,
          action: 'lesson_click',
        },
      });
    },
    [targetLocale],
  );

  const stepperContent = (
    <Stepper activeStep={currentMilestoneIndex} orientation='vertical' className={classes.stepper}>
      {milestones.map((milestone: MilestoneNode, index: number) => {
        const completed = milestoneCompletions[index];
        const clickable = canExpand[index];
        const isOpen = milestone.id === selectedMilestoneId || manuallyExpanded.has(milestone.id);

        return (
          <Step key={milestone.id} completed={completed} expanded={isOpen}>
            <StepLabel
              onClick={() => handleMilestoneClick(milestone, index)}
              className={clickable ? classes.clickableMilestone : ''}>
              {`${translate('Label.Milestone')} ${milestone.milestoneIndex}`}
            </StepLabel>
            <StepContent className={classes.stepContent}>
              <Collapse in={isOpen} timeout={500}>
                {milestone.children && milestone.children.length > 0 && (
                  <List className={classes.lessonList} dense>
                    {milestone.children.map((lesson: TreeNode) => {
                      const duration = lessonsWithDuration.get(lesson.id);
                      const isSelected = selected === lesson.id;
                      const isCompleted =
                        lesson.path && lessonStatuses[lesson.path] === Status.STATUS_ENUM_COMPLETED;
                      return (
                        <ListItem key={lesson.id}>
                          <DynamicallyRouted.Link
                            href={getCourseLinkForLocale(
                              targetLocale,
                              lesson.path || coursesRootPath,
                            )}
                            underline='none'
                            className={classes.lessonLink}
                            onClick={() => handleLessonClick(lesson, milestone)}>
                            <ListItemButton selected={isSelected}>
                              <ListItemText
                                primary={
                                  <React.Fragment>
                                    {lesson.value}
                                    {isCompleted && (
                                      <CheckIcon
                                        fontSize='small'
                                        color='secondary'
                                        className={classes.checkLesson}
                                      />
                                    )}
                                  </React.Fragment>
                                }
                                secondary={
                                  duration
                                    ? `${duration.duration} ${translate(duration.durationUnit)}`
                                    : ''
                                }
                              />
                            </ListItemButton>
                          </DynamicallyRouted.Link>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Collapse>
            </StepContent>
          </Step>
        );
      })}
    </Stepper>
  );

  if (showMobileMenu) {
    return (
      <React.Fragment>
        <CourseTitleWithMenu
          className={classes.mobileTitleAndMenu}
          showResetAlert={showResetAlert}
          onResetAlertChange={setShowResetAlert}
        />
        {stepperContent}
      </React.Fragment>
    );
  }

  return (
    <Grid id={rightNavID} item XSmall={3} Medium Large={4} height='100%'>
      <Grid className={classes.container}>
        <CourseTitleWithMenu
          className={classes.titleAndMenu}
          showResetAlert={showResetAlert}
          onResetAlertChange={setShowResetAlert}
        />
        <Grid item className={classes.scrollableContainer}>
          {stepperContent}
        </Grid>
      </Grid>
    </Grid>
  );
}
